import { useQuery } from '@tanstack/react-query'
import { getDownloadUrl, getPreviewThumbnailUrl } from './files-api.js'

/**
 * 画像 / PDF / 任意ファイルのプレビュー用署名URLを lazy に取得するフック。
 *
 * - 内部は getDownloadUrl (R2 署名URL発行) を呼ぶ
 * - 同じ fileId は react-query で dedupe（一覧で同じ画像が複数表示されても 1 回）
 * - 署名URLは 1 時間有効。staleTime も 50 分にして期限切れ前に再取得を促す
 */
export function usePreviewUrl(
  fileId: string,
  options: { enabled?: boolean } = {},
): {
  url: string | undefined
  isLoading: boolean
  error: Error | null
} {
  const enabled = options.enabled ?? true
  const q = useQuery({
    queryKey: ['preview-url', fileId],
    queryFn: () => getDownloadUrl(fileId),
    enabled,
    staleTime: 50 * 60 * 1000, // 50 分（署名 URL 寿命 1h より短く）
    gcTime: 60 * 60 * 1000,
  })
  return {
    url: q.data?.url,
    isLoading: q.isLoading,
    error: q.error,
  }
}

/**
 * 動画ファイルの署名済みサムネイル URL を lazy に取得するフック。
 *
 * - 一覧表示時はサーバ側で URL を発行しないので、画面に入った行から個別に取得する
 * - 同じ fileId は react-query で dedupe（行が重複して描画されても 1 回）
 * - Stream の Playback Token は短命だが、サムネ URL も同じトークンを使うため、
 *   実用上 50 分は安全にキャッシュできる
 */
export function useStreamThumbnailUrl(
  fileId: string,
  options: { enabled?: boolean } = {},
): {
  url: string | undefined
  isLoading: boolean
  error: Error | null
} {
  const enabled = options.enabled ?? true
  const q = useQuery({
    queryKey: ['stream-thumbnail-url', fileId],
    queryFn: () => getPreviewThumbnailUrl(fileId),
    enabled,
    staleTime: 50 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  })
  return {
    url: q.data?.url,
    isLoading: q.isLoading,
    error: q.error,
  }
}
